import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject, throwError } from 'rxjs';
import {
    tap,
    map,
    filter,
    switchMap,
    catchError,
    expand,
    takeWhile,
    takeUntil,
    take,
    withLatestFrom,
    first,
} from 'rxjs/operators';

import {
    Bank, Invoice, OrderPartialGiftTicketData, Order, OrderRefundData, OrderTracking, Receipt, Orders, Receipts,
} from './order.models';

import {
    ApiBanksResponse,
    ApiCardAdjustments,
    ApiCardAdjustmentsPayload,
    ApiCouponPayload,
    ApiCouponResponse,
    ApiPaymentBundle,
    ApiPaymentMethodsParams,
    ApiPromoCodePayload,
    ApiRefundResponse,
} from '@api/_api/api-order/api-order.models';
import { ApiOrderService } from '@api/_api/api-order/api-order.service';
import { OrderModelsTransformer } from '@api/order/order.transformer';
import { PhysicalStoreService } from '@api/store-locator/physical-store/physical-store.service';
import { createEcommerceError } from '@core/application/use-cases/errors/create-ecommerce-error.use-case';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { StoreAndLanguage } from '@core/domain/state/core.state';
import { LoadLibrary } from '@core/infrastructure/state/core-commands.actions';
import { AppEffects } from '@core/infrastructure/state/core.effects';
import { getLibraries, getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { AddressService } from '@features/address';
import { DiscountDetails, PaymentMethod } from '@features/payment';
import { DuttiDateFormat, DuttiDate } from '@frontend/common/duttidate';
import { StaticResourceService } from '@shared/services/static-resources/static-resource.service';
import { getUserSelector, selectUserIsFeel } from '@shared/user/store';

@Injectable()
export class OrderService {
    private readonly storeAndLanguage$: Observable<StoreAndLanguage> = this.store.select(getStoreAndLanguage)
        .pipe(first());

    constructor(
        private readonly apiOrderService: ApiOrderService,
        private readonly store: Store,
        private readonly staticResourceService: StaticResourceService,
        private readonly addressService: AddressService,
        private readonly physicalStoreService: PhysicalStoreService,
        private readonly appEffects: AppEffects,
        @Inject(WINDOW) private readonly window: CommonWindow,
    ) {}

    public setCardAdjustments(
        storeId: string,
        orderId: string,
        langId: string,
        payload: ApiCardAdjustmentsPayload,
        useInditex: boolean
    ): Observable<DiscountDetails> {
        const source = useInditex ? this.apiOrderService.setCardAdjustmentsItx(orderId, payload)
            : this.apiOrderService.setCardAdjustments(storeId, orderId, langId, payload).pipe(
                map((apiCardAdjustments: ApiCardAdjustments) => {
                    const discountDetails: DiscountDetails = OrderModelsTransformer.ApiCardAdjustmentsToDiscountDetails(
                        apiCardAdjustments
                    );

                    discountDetails.values.paymentMethodType = payload[0].paymentMethodType;

                    return discountDetails;
                })
            );

        return source;
    }

    public unsetCardAdjustments(
        storeId: string,
        orderId: string,
        langId: string,
        payload: ApiCardAdjustmentsPayload,
        useInditex: boolean,
        cardIndex?: number
    ): Observable<DiscountDetails> {
        const source = useInditex ? this.apiOrderService.unsetCardAdjustmentsItx(orderId, cardIndex)
            : this.apiOrderService.unsetCardAdjustments(storeId, orderId, langId, payload).pipe(
                map((apiCardAdjustments: ApiCardAdjustments) => {
                    const discountDetails: DiscountDetails = OrderModelsTransformer.ApiCardAdjustmentsToDiscountDetails(
                        apiCardAdjustments
                    );
                    discountDetails.values.paymentMethodType = payload[0].paymentMethodType;

                    return discountDetails;
                })
            );
        return source;
    }

    /**
     * Obtiene el listado de productos de una categoría
     *
     * @param storeId Identificador de la tienda
     * @param langId  Identificador del idioma
     * @param orderId Identificador del pedido
     * @returns
     */
    public paymentMethods(
        storeId: string,
        langId: string,
        orderId: string,
        appId: number,
        repay?: boolean
    ): Observable<PaymentMethod[]> {
        const params: ApiPaymentMethodsParams = {
            storeId, langId, orderId, appId, repay,
        };

        return this.apiOrderService.paymentMethods(params).pipe(
            filter((methods) => methods != null),
            tap((methods) => this.addImage(methods.paymentMethods)),
            map((methods)=> this.groupIfRequired(methods.paymentMethods))
        );
    }

    public isApplePaySupported() {
        this.store.dispatch(
            LoadLibrary({
                library: 'applePay',
                src: 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js',
            })
        );
        // Waiting library to load
        return this.appEffects.loadLibrarySuccess$.pipe(
            switchMap(() => this.store.select(getLibraries)),
            filter((libraries) => libraries.applePay?.loaded),
            first(),
            map(() => this.window.ApplePaySession?.canMakePayments() && !!this.window.PaymentRequest),
        );
    }

    public setUnboundPayment(storeId: string, langId: string, orderId: string, data: ApiPaymentBundle) {
        return this.apiOrderService.setUnboundPayment(storeId, langId, orderId, data);
    }

    public getOrderById(orderId: number, subOrders: boolean) {
        return this.storeAndLanguage$.pipe(
            switchMap((storeAndLang: StoreAndLanguage) =>
                this.apiOrderService.getOrderById(storeAndLang.storeId, storeAndLang.languageId, orderId, subOrders).pipe(
                    catchError((error: unknown) => {
                        return this.processError(error);
                    })
                ))
        );
    }

    public getOrderByIdGuest(orderId: number, guestId: string) {
        return this.storeAndLanguage$.pipe(
            switchMap((storeAndLang: StoreAndLanguage) =>
                this.apiOrderService.getOrderByIdGuest(storeAndLang.storeId, storeAndLang.languageId, orderId, guestId).pipe(
                    catchError((error: unknown) => {
                        return this.processError(error);
                    })
                ))
        );
    }

    public getOrders(index?): Observable<Orders> {
        return this.storeAndLanguage$.pipe(
            switchMap((storeAndLang: StoreAndLanguage) =>
                this.apiOrderService.getOrders(storeAndLang.storeId, storeAndLang.languageId, index).pipe(
                    withLatestFrom(
                        this.store.select(selectUserIsFeel)
                    ),
                    switchMap(([ordersResponse, isFeel]) => {
                        if (isFeel && ordersResponse.orders.length > 0) {
                            return combineLatest(ordersResponse.orders.map(order =>
                                this.apiOrderService
                                    .getOrderPoints(storeAndLang.storeId, storeAndLang.languageId, order.id as string).pipe(
                                        map(({ totalPoints }) => ({
                                            ...order,
                                            totalPoints,
                                        })),
                                        catchError(() => of(order)),
                                    ))).pipe(
                                map(orders => ({
                                    ...ordersResponse,
                                    orders,
                                }))
                            );
                        }
                        return of(ordersResponse);
                    })
                ))
        );
    }

    public getOrdersStore(index): Observable<Receipts> {
        return combineLatest([
            this.storeAndLanguage$,
            this.store.select(getUserSelector).pipe(first()),
        ])
            .pipe(
                switchMap(([storeAndLang, user]) => {
                    const today = new DuttiDate();
                    const todayFormatted = today.format(DuttiDateFormat.YEAR_MONTH_DAY);

                    return this.apiOrderService.getOrdersStore(
                        user.identity.userId,
                        storeAndLang.storeId,
                        storeAndLang.languageId,
                        todayFormatted,
                        index
                    ).pipe(
                        withLatestFrom(
                            this.store.select(selectUserIsFeel)
                        ),
                        switchMap(([receiptsResponse, isFeel]) => {
                            if (isFeel && receiptsResponse.ListReceiptSummary.length > 0) {
                                return combineLatest(receiptsResponse.ListReceiptSummary.map(receipt =>
                                    this.apiOrderService.getOrderPoints(
                                        storeAndLang.storeId,
                                        storeAndLang.languageId,
                                        receipt.ReceiptUID
                                    ).pipe(
                                        map(({ totalPoints }) => ({
                                            ...receipt,
                                            totalPoints,
                                        })),
                                        catchError(() => of(receipt)),
                                    ))).pipe(
                                    map(ListReceiptSummary => ({
                                        ...receiptsResponse,
                                        ListReceiptSummary,
                                    }))
                                );
                            }
                            return of(receiptsResponse);
                        })
                    );
                })
            );
    }

    private addImage(methods) {
        return methods.map((method, index) => {
            // eslint-disable-next-line no-param-reassign
            method.id = index;
            // eslint-disable-next-line no-param-reassign
            method.name = this.RenamePayment(method);
            // eslint-disable-next-line no-param-reassign
            method.imageURL = this.staticResourceService.getPaymentLogo(method);
            return method;
        });
    }

    private RenamePayment(method) {
        return method.name;
    }

    private groupIfRequired(methods: PaymentMethod[]) {
        const groupMethods = methods.some(method => method.name.includes('Alipay'));
        if (groupMethods) {
            return this.groupMethods(methods);
        }
        return methods;
    }

    public addPromoCode(storeAndLanguage: StoreAndLanguage, orderId: string, promoCode: string): Observable<any> {
        const payload: ApiPromoCodePayload = {
            promoCode,
        };

        return this.apiOrderService.addPromoCode(storeAndLanguage.storeId, storeAndLanguage.languageId, orderId, payload);
    }

    public deletePromoCode(storeAndLanguage: StoreAndLanguage, orderId: string, promoCode: string): Observable<any> {
        const payload: ApiPromoCodePayload = {
            promoCode,
        };

        return this.apiOrderService.deletePromoCode(storeAndLanguage.storeId, storeAndLanguage.languageId, orderId, payload);
    }

    public addCoupon(storeId: string, orderId: string, coupon: string):
    Observable<ApiCouponResponse> {
        const payload: ApiCouponPayload = {
            couponCode: coupon,
        };

        return this.apiOrderService.addCoupon(storeId, orderId, payload);
    }

    public deleteCoupon(storeId: string, orderId: string, coupon: string):
    Observable<ApiCouponResponse> {
        const payload: ApiCouponPayload = {
            couponCode: coupon,
        };

        return this.apiOrderService.deleteCoupon(storeId, orderId, payload);
    }

    public printEgui(languageId: string, orderId: any): Observable<any> {
        return this.apiOrderService.printEgui(orderId, languageId);
    }

    public getOrderPaymentMethodBanks(
        storeAndLanguage: StoreAndLanguage,
        orderId: string,
        paymentMethod: string
    ): Observable<Bank[]> {
        return this.apiOrderService.getOrderPaymentMethodBanks(
            storeAndLanguage.storeId,
            storeAndLanguage.languageId,
            orderId,
            paymentMethod
        ).pipe(
            map((data: ApiBanksResponse) => data?.banks)
        );
    }

    public tracking(storeId: string, languageId: string, orderId: string): Observable<OrderTracking> {
        return this.apiOrderService.tracking(storeId, languageId, orderId)
            .pipe(
                map((orderTracking) => OrderModelsTransformer.ApiOrderTracking(orderTracking)),
            );
    }

    private haveMoreInvoice(invoiceResponse) {
        return invoiceResponse.invoices.length > 0 && invoiceResponse.invoices.length === invoiceResponse.howMany;
    }

    // TODO ECOMDUTI-20991 INVOICE PODRIA ESTAR EN UNA STORE
    public getInvoice(orderId: number, isReturnInvoice: boolean): Observable<Invoice> {
        const finish = new Subject<void>();

        return new Observable<any>(subscriber => {
            this.storeAndLanguage$.pipe(
                switchMap((storeAndLanguage) =>
                    this.apiOrderService.getInvoices(storeAndLanguage.storeId, storeAndLanguage.languageId, 0).pipe(
                        expand((res) => this.apiOrderService.getInvoices(
                            storeAndLanguage.storeId,
                            storeAndLanguage.languageId,
                            res.index + res.howMany
                        )),
                        takeUntil(finish)
                    )),
            ).subscribe(res => {
                const invoice = res.invoices.find((resInvoice) => {
                    if (isReturnInvoice) {
                        return resInvoice.ordersId === orderId && resInvoice.rmaId !== null && resInvoice.rmaId !== 0;
                    }
                    return resInvoice.ordersId === orderId && (resInvoice.rmaId === null || resInvoice.rmaId === 0);
                });

                if (invoice) {
                    finish.next();
                    finish.complete();

                    subscriber.next(invoice);
                    subscriber.complete();
                }

                if (!this.haveMoreInvoice(res)) {
                    finish.next();
                    finish.complete();

                    subscriber.next();
                    subscriber.complete();
                }
            });
        });
    }

    // TODO ECOMDUTI-20991 INVOICE PODRIA ESTAR EN UNA STORE
    public getInvoices(index = 0): Observable<any> {
        return this.storeAndLanguage$.pipe(
            switchMap((storeAndLanguage) => {
                return this.apiOrderService.getInvoices(storeAndLanguage.storeId, storeAndLanguage.languageId, index).pipe(
                    expand((res) => this.apiOrderService.getInvoices(
                        storeAndLanguage.storeId,
                        storeAndLanguage.languageId,
                        res.index + res.howMany
                    )),
                    takeWhile(res => this.haveMoreInvoice(res), true)
                );
            }),
        );
    }

    public getOrderAddress(order: Order) {
        return this.storeAndLanguage$.pipe(switchMap(storeAndLanguage => {
            let addressId = null;

            // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
            if (!!order.shipping.shippingData.addressId) {
                addressId = order.shipping.shippingData.addressId;
                return this.addressService.getAddress(addressId);
            }
            // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
            if (!!order.shipping.shippingData.physicalStoreId) {
                addressId = order.shipping.shippingData.physicalStoreId;

                return this.physicalStoreService.getStore(storeAndLanguage.storeId, storeAndLanguage.languageId, addressId);
            }

            return of(null);
        }));
    }

    public static isPending(order: Partial<Order>): boolean {
        // TODO ECOMDUTI-20991 MOVER ESTE ARRAY A UN FICHERO DE CONTANTES
        return ['PQ', 'RP'].includes(order.status);
    }

    public static isCancelled(order: Partial<Order>): boolean {
        // TODO ECOMDUTI-20991 MOVER ESTE ARRAY A UN FICHERO DE CONTANTES
        return ['N', 'X'].includes(order.status);
    }

    public getPdfListTicketless(receiptUID: string) {
        return this.storeAndLanguage$.pipe(
            switchMap(storeAndLanguage =>
                this.apiOrderService.getPdfListTicketless(
                    receiptUID,
                    storeAndLanguage.storeId,
                    storeAndLanguage.languageId
                ).pipe(
                    map((data) => {
                        return data.ListDocuments || null;
                    })
                ))
        );
    }

    public cancelOrder(order: Partial<Order>) {
        return this.storeAndLanguage$.pipe(
            switchMap((storeAndLanguage) => {
                if (order.guestToken) {
                    return this.apiOrderService.cancelGuestOrder(
                        order.id,
                        storeAndLanguage.storeId,
                        storeAndLanguage.languageId,
                        order.guestToken
                    );
                }

                return this.apiOrderService.cancelOrder(order.id, storeAndLanguage.storeId, storeAndLanguage.languageId);
            })
        );
    }

    public cancelSubOrder(order: Partial<Order>, suborder: Order) {
        return this.storeAndLanguage$.pipe(
            switchMap((storeAndLanguage) => {
                const parentId = order.id;
                const suborderId = suborder.id;

                if (suborder.guestToken) {
                    return this.apiOrderService.cancelGuestSubOrder(
                        parentId,
                        suborderId,
                        storeAndLanguage.storeId,
                        storeAndLanguage.languageId,
                        suborder.guestToken
                    );
                }

                return this.apiOrderService.cancelSubOrder(
                    parentId,
                    suborderId,
                    storeAndLanguage.storeId,
                    storeAndLanguage.languageId
                );
            })
        );
    }

    public getStoreOrderDetail(receipt: Receipt): Observable<any> {
        return this.storeAndLanguage$.pipe(
            take(1),
            switchMap((storeAndLang) => this.apiOrderService
                .getStoreOrderDetail(
                    receipt.ReceiptUID,
                    receipt.QRCode,
                    storeAndLang.storeId,
                    storeAndLang.languageId
                ))
        );
    }

    public mapReceiptToOrder(receipt: Receipt): Observable<Partial<Order>> {
        return this.getStoreOrderDetail(receipt).pipe(
            map((details) => {
                const totalOrderAdapted = (receipt.Total * 100).toFixed(2).replace('.00', '');
                const order: Partial<Order> = {
                    id: `****${receipt.ReceiptUID.substring(receipt.ReceiptUID.length - 8)}`,
                    receiptUID: receipt.ReceiptUID,
                    stlocId: receipt.StoreId,
                    operationNumber: 0,
                    idCaja: details.Metadata.Store,

                    date: receipt.Date,
                    currency: receipt.Currency,
                    status: receipt.Total < 0 ? 'DEVO' : 'K',
                    suborders: [],
                    payment: [],
                    orderTracking: {},
                    shipping: {},
                    // biome-ignore lint/style/useNumberNamespace: <explanation>
                    totalOrder: parseInt(totalOrderAdapted.replace('.', '')),
                    totalPoints: receipt.totalPoints,
                    // address: receipt.Store?.Address || {},
                    hasSubOrders: false,
                    isPaperless: false,
                    items: details.HumanReadableData.Document.HumanReadableListDocumentLine.map((docLine) => {
                        const strAmount = (docLine.UnitGross * 100).toFixed(2).replace('.00', '');
                        return {
                            quantity: 1,
                            // biome-ignore lint/style/useNumberNamespace: <explanation>
                            unitPrice: parseInt(strAmount.toString().replace('.', '')),
                            name: docLine.Description,
                            reference: docLine.Code,
                            colorId: docLine.Color,
                            size: docLine.Size,
                            productType: 'Clothing',
                            fromStore: true,
                            imageUrl: docLine.ArticleData.ImageURL,
                            colors: [{
                                id: docLine.Color,
                            }],
                        };
                    }) || [],
                };
                return order;
            })
        );
    }

    public orderRefund(storeId: string, orderId: number, data: OrderRefundData): Observable<ApiRefundResponse> {
        return this.apiOrderService.orderRefund(
            storeId,
            orderId,
            data,
        );
    }

    public orderRefundGuest(
        storeId: string,
        orderId: number,
        data: OrderRefundData,
        tokenGuest: string
    ): Observable<ApiRefundResponse> {
        return this.apiOrderService.orderRefundGuest(
            storeId,
            orderId,
            data,
            tokenGuest
        );
    }

    public orderPartialGiftTicket(
        storeId: string,
        orderId: number,
        data: OrderPartialGiftTicketData
    ): Observable<ArrayBuffer> {
        return this.apiOrderService.orderPartialGiftTicket(storeId, orderId, data);
    }

    private groupMethods(methods) {
        const groupedMethods = [];
        const alipaySections = [
            {
                section: '1',
                kind: 'Alipay_Wallet',
                name: 'Alipay Wallet',
            },
            {
                section: '2',
                kind: 'Alipay_Banks',
                name: 'Alipay Banks',
            },
            {
                section: '3',
                kind: 'Alipay_International',
                name: 'Alipay International',
            },
        ];

        const orderedMethodsByKind = methods.sort((a, b) => {
            if (a.section > b.section) {
                return 1;
            }

            if (b.section > a.section) {
                return -1;
            }

            return 0;
        });
        const groupByKindMethods = this.groupBy(orderedMethodsByKind, 'kind');

        // biome-ignore lint/complexity/noForEach: <explanation>
        Object.entries(groupByKindMethods).forEach((methodsByKind) =>{
            const [kind, kindMethods] = [methodsByKind[0], methodsByKind[1]];

            const aliPaySection = alipaySections.find((value)=>{
                return value.kind === kind;
            });
            if (!aliPaySection) {
                // biome-ignore lint/complexity/noForEach: <explanation>
                Object.values(kindMethods).forEach(method => groupedMethods.push(method));
            }
            if (aliPaySection) {
                let globalMethod = kindMethods[0];
                if (Object.values(kindMethods).length > 1) {
                    globalMethod = {
                        ...kindMethods[0],
                        name: aliPaySection.name,
                        id: kindMethods[0].id,
                        code: kindMethods[0].code,
                        values: kindMethods,
                    };
                }
                groupedMethods.push(globalMethod);
            }
        });

        return groupedMethods;
    }

    private groupBy(xs, key) {
        const result = {};
        // biome-ignore lint/complexity/noForEach: <explanation>
        xs.forEach(x => {
            if (!result[x[key]]) {
                result[x[key]] = [];
            }
            result[x[key]].push(x);
        });
        return result;
    }

    private processError(error: any): Observable<never> {
        if (error instanceof HttpErrorResponse) {
            const ecommerceError = createEcommerceError(error);
            return throwError(() => ecommerceError);
        }
        return throwError(() => error);
    }
}
